import { AuthErrorField, ChangeEmailErrorKind } from '@griply/types';
import {
  getChangeEmailErrorField,
  getChangeEmailErrorMessageKey,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { FormInput } from 'shared/components/ui/form-input';

import * as Styled from './change-email-form.style';
import { useChangeEmailForm } from './use-change-email-form';

export type ChangeEmailFormProps = {
  currentEmail: string;
  onSubmit: (newEmail: string, password: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: ChangeEmailErrorKind;
};

export const ChangeEmailForm: React.FC<ChangeEmailFormProps> = ({
  currentEmail,
  onSubmit,
  onCancel,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();

  const {
    register,
    onSubmit: submitForm,
    errors,
    watch,
    isSameEmail,
  } = useChangeEmailForm(currentEmail, onSubmit);

  const [newEmailValue, passwordValue] = watch(['newEmail', 'password']);
  const submitDisabled = isLoading || !newEmailValue || !passwordValue;

  const errorField = error ? getChangeEmailErrorField(error) : null;
  const errorMessage = error ? t(getChangeEmailErrorMessageKey(error)) : null;

  const emailErrorMessage = useMemo(() => {
    if (isSameEmail) {
      return t('auth.change-email.errors.same-email');
    }
    if (errors.newEmail) {
      return t('login.invalid-email');
    }
    return errorField === AuthErrorField.Email ? errorMessage : null;
  }, [isSameEmail, errors.newEmail, errorField, errorMessage, t]);
  const passwordErrorMessage =
    errorField === AuthErrorField.Password ? errorMessage : null;
  const formErrorMessage =
    errorField === AuthErrorField.Form ? errorMessage : null;

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.Fields>
        <Styled.FieldContainer>
          <Styled.Label htmlFor="newEmail">
            {t('auth.change-email.fields.new-email.label')}
          </Styled.Label>
          <FormInput
            {...register('newEmail')}
            id="newEmail"
            type="email"
            autoComplete="email"
            placeholder={t('auth.change-email.fields.new-email.placeholder')}
            error={emailErrorMessage ?? undefined}
            disabled={isLoading}
          />
          {!!emailErrorMessage && (
            <Styled.ErrorMessage>{emailErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>

        <Styled.FieldContainer>
          <Styled.Label htmlFor="password">
            {t('auth.change-email.fields.password.label')}
          </Styled.Label>
          <FormInput
            {...register('password')}
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder={t('auth.change-email.fields.password.placeholder')}
            error={passwordErrorMessage ?? undefined}
            disabled={isLoading}
          />
          {!!passwordErrorMessage && (
            <Styled.ErrorMessage>{passwordErrorMessage}</Styled.ErrorMessage>
          )}
          {!!formErrorMessage && (
            <Styled.ErrorMessage>{formErrorMessage}</Styled.ErrorMessage>
          )}
        </Styled.FieldContainer>

        <Styled.Interactions>
          <Button
            variant={Variants.Contained}
            size={Sizes.Medium}
            color={Colors.TextPrimary}
            onClick={onCancel}
            disabled={isLoading}
            type="button"
          >
            {t('forms.change-password.buttons.cancel.label')}
          </Button>
          <Button
            variant={Variants.Contained}
            color={Colors.Primary}
            size={Sizes.Medium}
            type="submit"
            disabled={submitDisabled}
            disabledBackgroundOnly={isLoading}
          >
            {isLoading ? (
              <Styled.Loader color="primary" size={14} />
            ) : (
              t('auth.change-email.submit')
            )}
          </Button>
        </Styled.Interactions>
      </Styled.Fields>
    </Styled.Form>
  );
};
